import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { message } from 'antd'
import type { LoginRequest, LoginResponse, User, Notification } from '@/types/auth'
import type {
  // 积分管理
  PointsSummary,
  PointsTransaction,
  UserPointsDetail,
  UserPointsTransactions,
  PointsTransactionsList,
  AdjustPointsRequest,
  AdjustPointsResponse,
  TransactionType,
  TransactionsQueryParams,
  // 配置管理
  RefundPolicy,
  RefundPoliciesResponse,
  BookingRules,
  BookingRulesResponse,
  UpdateBookingRulesRequest,
  BonusTier,
  BonusTiersResponse,
  ExchangeRate,
  UpdateExchangeRateRequest,
  // 分页
  PageQueryParams,
  SessionsListResponse,
  SessionsQueryParams,
  TicketingSessionDetail,
} from '@/types/ticketing'

// ARCHIVED_ROUTE 后端中间件统一错误码（与 services/admin-portal-api/src/core/errors/error_codes.py 一致）
// 命中该错误码表示路由已迁移至新后台，前端应弹友好提示并停止后续错误处理。
const ARCHIVED_ROUTE_ERROR_CODE = 10011
// 防止同一请求触发多次 toast（例如并发重试）
let lastArchivedToastAt = 0

// API configuration (使用相对路径通过 Vite 代理)
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 自动解包统一错误码格式的响应
    // 后端统一格式: { error_code: 0, message: "...", data: {...} }
    if (response.data && typeof response.data === 'object' && 'error_code' in response.data) {
      // 检查错误码
      if (response.data.error_code !== 0) {
        // 错误码非0，抛出错误
        return Promise.reject({
          response: {
            status: response.status,
            data: response.data,
          },
          message: response.data.message || '请求失败',
        })
      }
      // 成功响应，解包 data 字段
      response.data = response.data.data
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 410 Gone (归档路由)
    // 后端中间件 archived_guard 命中后返回 410 + {error_code: 10011}
    // 前端弹一次友好提示，并打上 _archived 标记让上层调用方识别（避免重复 toast）
    if (error.response?.status === 410) {
      const errorBody = error.response.data
      const isArchived =
        errorBody?.error_code === ARCHIVED_ROUTE_ERROR_CODE ||
        errorBody?.error?.code === 'ARCHIVED_ROUTE'
      if (isArchived) {
        const now = Date.now()
        if (now - lastArchivedToastAt > 1500) {
          lastArchivedToastAt = now
          message.warning({
            content: '该功能已迁移至新后台，请前往新后台操作。',
            duration: 4,
            key: 'archived-route-toast',
          })
        }
        // 标记为已归档错误，让上层 catch 分支可识别并提前 return
        return Promise.reject({
          ...error,
          isArchivedRoute: true,
          response: error.response,
        })
      }
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = localStorage.getItem('refresh_token')

      // Try to refresh token if available
      if (refreshToken) {
        try {
          const response = await authApi.refreshToken(refreshToken)
          const { access_token } = response.data

          localStorage.setItem('access_token', access_token)
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          return apiClient(originalRequest)
        } catch (refreshError) {
          // Refresh failed, redirect to login
          console.error('Token refresh failed:', refreshError)
          localStorage.clear()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, redirect to login immediately
        console.warn('No refresh token available, redirecting to login')
        localStorage.clear()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  // Set auth header
  setAuthHeader: (token: string) => {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  },

  // Clear auth header
  clearAuthHeader: () => {
    delete apiClient.defaults.headers.common['Authorization']
  },

  // Login
  login: async (credentials: LoginRequest): Promise<AxiosResponse<LoginResponse>> => {
    return apiClient.post('/auth/login', credentials)
  },

  // Register
  register: async (userData: Partial<User>): Promise<AxiosResponse<User>> => {
    return apiClient.post('/auth/register', userData)
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<AxiosResponse<{ access_token: string }>> => {
    return apiClient.post('/auth/refresh', { refresh_token: refreshToken })
  },

  // Logout
  logout: async (token: string): Promise<AxiosResponse<void>> => {
    return apiClient.post('/auth/logout', { token })
  },

  // Get current user
  getCurrentUser: async (): Promise<AxiosResponse<User>> => {
    return apiClient.get('/auth/me')
  },

  // Get user permissions
  getUserPermissions: async (): Promise<AxiosResponse<string[]>> => {
    return apiClient.get('/auth/permissions')
  },

  // Get notifications
  getNotifications: async (): Promise<AxiosResponse<Notification[]>> => {
    return apiClient.get('/notifications')
  },

  // Mark notification as read
  markNotificationAsRead: async (id: string): Promise<AxiosResponse<void>> => {
    return apiClient.patch(`/notifications/${id}/read`)
  },

  // Mark all notifications as read
  markAllNotificationsAsRead: async (): Promise<AxiosResponse<void>> => {
    return apiClient.patch('/notifications/read-all')
  },

  // ========== 多渠道登录 (Multi-Channel Auth) ==========
  // Email + Password login
  loginWithEmail: async (email: string, password: string): Promise<AxiosResponse<LoginResponse>> => {
    return apiClient.post('/auth/login/email', { email, password })
  },

  // Phone + Verification code login (auto-register)
  loginWithPhone: async (phone: string, code: string): Promise<AxiosResponse<LoginResponse>> => {
    return apiClient.post('/auth/login/phone', { phone, code })
  },

  // Email registration
  registerWithEmail: async (data: {
    email: string
    password: string
    username: string
    code: string
  }): Promise<AxiosResponse<LoginResponse>> => {
    return apiClient.post('/auth/register/email', data)
  },

  // Phone registration
  registerWithPhone: async (data: {
    phone: string
    password: string
    username: string
    code: string
  }): Promise<AxiosResponse<LoginResponse>> => {
    return apiClient.post('/auth/register/phone', data)
  },

  // ========== 验证码管理 (Verification Code) ==========
  // Send verification code
  sendVerificationCode: async (type: 'phone' | 'email', target: string, action: string) => {
    return apiClient.post('/auth/verification/send', { type, target, action })
  },

  // Validate verification code
  validateVerificationCode: async (type: 'phone' | 'email', target: string, code: string) => {
    return apiClient.post('/auth/verification/validate', { type, target, code })
  },

  // ========== 密码管理 (Password Management) ==========
  // Forgot password
  forgotPassword: async (identifier: string, type: 'email' | 'phone') => {
    return apiClient.post('/auth/password/forgot', { identifier, type })
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string) => {
    return apiClient.post('/auth/password/reset', { token, new_password: newPassword })
  },

  // Change password (authenticated)
  changePassword: async (oldPassword: string, newPassword: string) => {
    return apiClient.post('/auth/password/change', {
      old_password: oldPassword,
      new_password: newPassword,
    })
  },

  // ========== 账号绑定/解绑 (Account Binding) ==========
  // Bind phone
  bindPhone: async (phone: string, code: string) => {
    return apiClient.post('/auth/bind/phone', { phone, code })
  },

  // Bind email
  bindEmail: async (email: string, code: string) => {
    return apiClient.post('/auth/bind/email', { email, code })
  },

  // Unbind phone
  unbindPhone: async (password: string) => {
    return apiClient.delete('/auth/unbind/phone', { data: { password } })
  },

  // Unbind email
  unbindEmail: async (password: string) => {
    return apiClient.delete('/auth/unbind/email', { data: { password } })
  },

  // ========== 会话管理 (Session Management) ==========
  // Get all sessions
  getSessions: async () => {
    return apiClient.get('/auth/sessions/')
  },

  // Terminate session
  terminateSession: async (sessionId: string) => {
    return apiClient.delete(`/auth/sessions/${sessionId}`)
  },

  // Terminate all sessions except current
  terminateAllSessions: async () => {
    return apiClient.delete('/auth/sessions/all')
  },
}

export interface VenueAdmin {
  id: number
  username: string
  email: string
  phone?: string | null
  full_name?: string | null
  merchant_id?: string | null
  venue_id?: string | null
  venue_name?: string | null
  venue_address?: string | null
  is_active: boolean
  created_at: string
}

export interface VenueAdminList {
  items: VenueAdmin[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface VenueBindingPayload {
  merchant_id: string
  venue_id: string
  venue_name: string
  venue_address?: string
}

interface VenueBindingUser {
  id: number
  username: string
  email: string
  merchant_id?: string
  venue_id?: string
  venue_name?: string
  venue_address?: string
}

export const venueAdminApi = {
  list: async (params?: {
    page?: number
    page_size?: number
    search?: string
    merchant_id?: string
    venue_id?: string
  }): Promise<AxiosResponse<VenueAdminList>> => {
    return apiClient.get('/admin/venue-admins', { params })
  },

  bindVenue: async (
    userId: number,
    payload: VenueBindingPayload
  ): Promise<AxiosResponse<{ message: string; user: VenueBindingUser }>> => {
    return apiClient.put(`/admin/venue-admins/${userId}/venue`, payload)
  },

  unbindVenue: async (
    userId: number
  ): Promise<AxiosResponse<{ message: string; user: VenueBindingUser }>> => {
    return apiClient.delete(`/admin/venue-admins/${userId}/venue`)
  },
}

// User management API
export const userApi = {
  // Get users list
  getUsers: async (params?: { page?: number; page_size?: number; search?: string; role?: string; is_active?: boolean; is_verified?: boolean; kyc_status?: string; include_deleted?: boolean }) => {
    return apiClient.get('/users', { params })
  },

  // Get user statistics
  getStats: async (params?: { search?: string; role?: string }) => {
    return apiClient.get('/users/stats', { params })
  },

  // Get user by ID
  getUser: async (id: number) => {
    return apiClient.get(`/users/${id}`)
  },

  // Create user
  createUser: async (userData: Partial<User>) => {
    return apiClient.post('/users', userData)
  },

  // Update user
  updateUser: async (id: number, userData: Partial<User>) => {
    return apiClient.put(`/users/${id}`, userData)
  },

  // Delete user
  deleteUser: async (id: number) => {
    return apiClient.delete(`/users/${id}`)
  },

  // Assign role to user
  assignRole: async (userId: number, roleId: number) => {
    return apiClient.post(`/users/${userId}/roles`, { role_id: roleId })
  },

  // Remove role from user
  removeRole: async (userId: number, roleId: number) => {
    return apiClient.delete(`/users/${userId}/roles/${roleId}`)
  },

  // ========== 绑定管理 (Binding Management) ==========
  // Send verification code for binding
  sendVerificationCode: async (userId: number, data: { type: 'email' | 'phone'; target: string; action: string }) => {
    return apiClient.post(`/users/${userId}/verification/send`, data)
  },

  // Bind email to user
  bindEmail: async (userId: number, data: { email: string; verification_code: string }) => {
    return apiClient.post(`/users/${userId}/bind/email`, data)
  },

  // Bind phone to user
  bindPhone: async (userId: number, data: { phone: string; verification_code: string }) => {
    return apiClient.post(`/users/${userId}/bind/phone`, data)
  },

  // Unbind phone from user
  unbindPhone: async (userId: number) => {
    return apiClient.delete(`/users/${userId}/unbind/phone`)
  },

  // Unbind WeChat from user
  unbindWeChat: async (userId: number) => {
    return apiClient.delete(`/users/${userId}/unbind/wechat`)
  },

  // Bind WeChat to user
  bindWeChat: async (userId: number, data: { union_id: string; nickname?: string; avatar_url?: string }) => {
    return apiClient.post(`/users/${userId}/bind/wechat`, data)
  },

  // ========== 管理员功能 (Admin Functions) ==========
  // Admin: Bind WeChat to user
  adminBindWechat: async (
    userId: number,
    data: {
      union_id: string
      openid?: string
      nickname?: string
      avatar_url?: string
    }
  ) => {
    return apiClient.post(`/admin/users/${userId}/bind-wechat`, data)
  },

  // Admin: Update user email
  adminUpdateEmail: async (userId: number, email: string) => {
    return apiClient.put(`/admin/users/${userId}/email`, { email })
  },

  // Admin: Update user phone
  adminUpdatePhone: async (userId: number, phone: string) => {
    return apiClient.put(`/admin/users/${userId}/phone`, { phone })
  },

  // Admin: Unbind WeChat from user
  adminUnbindWechat: async (userId: number) => {
    return apiClient.delete(`/admin/users/${userId}/wechat`)
  },

  // Admin: Reset user password
  adminResetPassword: async (userId: number, newPassword?: string) => {
    return apiClient.post(`/admin/users/${userId}/reset-password`, {
      new_password: newPassword,
    })
  },

  // Admin: Lock user account
  adminLockAccount: async (userId: number) => {
    return apiClient.post(`/admin/users/${userId}/lock`)
  },

  // Admin: Unlock user account
  adminUnlockAccount: async (userId: number) => {
    return apiClient.post(`/admin/users/${userId}/unlock`)
  },
}

// Script management API
export const scriptApi = {
  // Get scripts list
  getScripts: async (params?: { page?: number; page_size?: number; search?: string; status?: string; include_deleted?: boolean }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/playwright/scripts/', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // Get script by ID
  getScript: async (id: number) => {
    return apiClient.get(`/playwright/scripts/${id}`)
  },

  // Create script
  createScript: async (scriptData: any) => {
    return apiClient.post('/playwright/scripts/', scriptData)
  },

  // Update script
  updateScript: async (id: number, scriptData: any) => {
    return apiClient.put(`/playwright/scripts/${id}`, scriptData)
  },

  // Delete script
  deleteScript: async (id: number) => {
    return apiClient.delete(`/playwright/scripts/${id}`)
  },

  // Submit script for review
  submitForReview: async (id: number) => {
    return apiClient.post(`/playwright/scripts/${id}/submit`)
  },

  // Approve script
  approveScript: async (id: number, reviewNotes?: string) => {
    return apiClient.post(`/playwright/scripts/${id}/approve`, null, {
      params: reviewNotes ? { review_notes: reviewNotes } : {}
    })
  },

  // Reject script (review_notes is required)
  rejectScript: async (id: number, reviewNotes: string) => {
    return apiClient.post(`/playwright/scripts/${id}/reject`, null, {
      params: { review_notes: reviewNotes }
    })
  },

  // Export script package (deploy to venue)
  exportScript: async (id: number, merchantId?: number) => {
    const params = merchantId ? { merchant_id: merchantId } : {}
    return apiClient.post(`/playwright/scripts/${id}/export`, null, { params })
  },

  // Get script statistics
  getScriptStatistics: async () => {
    return apiClient.get(`/playwright/scripts/statistics`)
  },
}

// Venue management API
export const venueApi = {
  // Get venues list
  getVenues: async (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    return apiClient.get('/venues', { params })
  },

  // Get venue by ID
  getVenue: async (id: number) => {
    return apiClient.get(`/venues/${id}`)
  },

  // Create venue
  createVenue: async (venueData: any) => {
    return apiClient.post('/venues', venueData)
  },

  // Update venue
  updateVenue: async (id: number, venueData: any) => {
    return apiClient.put(`/venues/${id}`, venueData)
  },

  // Delete venue
  deleteVenue: async (id: number) => {
    return apiClient.delete(`/venues/${id}`)
  },
}

// Analytics API
export const analyticsApi = {
  // Get dashboard stats
  getDashboardStats: async (params?: { period?: string; refresh?: boolean }) => {
    return apiClient.get('/analytics/dashboard', { params })
  },

  // Get user analytics
  getUserAnalytics: async (params?: { period?: string; start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/users', { params })
  },

  // Get script analytics
  getScriptAnalytics: async (params?: { period?: string; start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/scripts', { params })
  },

  // Get venue analytics
  getVenueAnalytics: async (params?: { period?: string; start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/venues', { params })
  },

  // Get merchant analytics
  getMerchantAnalytics: async (params?: { period?: string; start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/merchants', { params })
  },

  // Get revenue analytics
  getRevenueAnalytics: async (params?: { period?: string; start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/revenue', { params })
  },

  // Get ad analytics
  getAdAnalytics: async (params?: {
    start_date?: string;
    end_date?: string;
    metric_type?: 'daily' | 'weekly' | 'monthly';
  }) => {
    return apiClient.get('/ads/analytics/overview', { params })
  },

  // Get ad performance metrics
  getAdPerformance: async (params?: {
    metric_type?: 'daily' | 'weekly' | 'monthly';
    campaign_id?: string;
  }) => {
    return apiClient.get('/ads/analytics/performance', { params })
  },
}

// Script management API (Playwright)
export const playwrightScriptApi = {
  // Get scripts list
  getScripts: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    playwright_id?: number;
  }) => {
    return apiClient.get('/playwright/scripts', { params })
  },

  // Get script by ID
  getScript: async (id: number) => {
    return apiClient.get(`/playwright/scripts/${id}`)
  },

  // Create script
  createScript: async (scriptData: any) => {
    return apiClient.post('/playwright/scripts', scriptData)
  },

  // Update script
  updateScript: async (id: number, scriptData: any) => {
    return apiClient.put(`/playwright/scripts/${id}`, scriptData)
  },

  // Delete script
  deleteScript: async (id: number) => {
    return apiClient.delete(`/playwright/scripts/${id}`)
  },

  // Submit script for review
  submitScript: async (id: number) => {
    return apiClient.post(`/playwright/scripts/${id}/submit`)
  },

  // Get script characters
  getScriptCharacters: async (id: number) => {
    return apiClient.get(`/playwright/scripts/${id}/characters`)
  },

  // Get script stats
  getScriptStats: async (id: number) => {
    return apiClient.get(`/playwright/scripts/${id}/stats`)
  },
}

// Script Package management API
export const scriptPackageApi = {
  // Get script packages list (for ConfigBlockManagement)
  getScriptPackages: async (params?: {
    page?: number;
    limit?: number;
  }) => {
    return apiClient.get('/playwright/script-packages/', { params })
  },

  // Get script packages by script_id
  getByScriptId: async (scriptId: number) => {
    return apiClient.get('/playwright/script-packages/', {
      params: { script_id: scriptId, page: 1, page_size: 100 }
    })
  },

  // Get script package by ID
  getScriptPackage: async (id: number) => {
    return apiClient.get(`/playwright/script-packages/${id}`)
  },

  // Get script package versions
  getPackageVersions: async (packageId: string) => {
    return apiClient.get(`/playwright/script-packages/versions/${packageId}`)
  },
}

// Character management API
export const characterApi = {
  // Get characters list
  getCharacters: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    script_id?: number;
  }) => {
    return apiClient.get('/playwright/characters', { params })
  },

  // Get character by ID
  getCharacter: async (id: number) => {
    return apiClient.get(`/playwright/characters/${id}`)
  },

  // Create character
  createCharacter: async (characterData: any) => {
    return apiClient.post('/playwright/characters', characterData)
  },

  // Update character
  updateCharacter: async (id: number, characterData: any) => {
    return apiClient.put(`/playwright/characters/${id}`, characterData)
  },

  // Delete character
  deleteCharacter: async (id: number) => {
    return apiClient.delete(`/playwright/characters/${id}`)
  },

  // Reorder characters
  reorderCharacters: async (id: number, newOrder: number) => {
    return apiClient.post(`/playwright/characters/${id}/reorder`, { new_order: newOrder })
  },
}

// Merchant management API
export const merchantApi = {
  // Get merchants list
  getMerchants: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
  }) => {
    return apiClient.get('/merchant/merchants', { params })
  },

  // Get merchant by ID
  getMerchant: async (id: number) => {
    return apiClient.get(`/merchant/merchants/${id}`)
  },

  // Create merchant
  createMerchant: async (merchantData: any) => {
    return apiClient.post('/merchant/merchants', merchantData)
  },

  // Update merchant
  updateMerchant: async (id: number, merchantData: any) => {
    return apiClient.put(`/merchant/merchants/${id}`, merchantData)
  },

  // Delete merchant
  deleteMerchant: async (id: number) => {
    return apiClient.delete(`/merchant/merchants/${id}`)
  },

  // Verify merchant
  verifyMerchant: async (id: number) => {
    return apiClient.post(`/merchant/merchants/${id}/verify`)
  },

  // Get merchant stats
  getMerchantStats: async (id: number) => {
    return apiClient.get(`/merchant/merchants/${id}/stats`)
  },
}

// Merchant member management API
export const merchantMemberApi = {
  // Get accessible merchants for current user
  getAccessibleMerchants: async () => {
    return apiClient.get('/merchant/members/accessible')
  },

  // Get current user's merchant roles
  getMyMerchantRoles: async (params?: { include_inactive?: boolean }) => {
    return apiClient.get('/merchant/members/me/roles', { params })
  },

  // Switch merchant context
  switchMerchantContext: async (merchantId: number) => {
    return apiClient.post(`/merchant/members/switch/${merchantId}`)
  },

  // Get merchant members list
  getMerchantMembers: async (
    merchantId: number,
    params?: {
      page?: number
      limit?: number
      role_type?: string
      status_filter?: string
    }
  ) => {
    return apiClient.get(`/merchant/members/${merchantId}/members`, { params })
  },

  // Add merchant member
  addMerchantMember: async (
    merchantId: number,
    memberData: {
      user_id: number
      role_type: string
      expires_at?: string
      permissions?: Record<string, any>
    }
  ) => {
    return apiClient.post(`/merchant/members/${merchantId}/members`, memberData)
  },

  // Update merchant member
  updateMerchantMember: async (
    merchantId: number,
    userId: number,
    updateData: {
      role_type?: string
      expires_at?: string
      permissions?: Record<string, any>
      is_active?: boolean
    }
  ) => {
    return apiClient.put(`/merchant/members/${merchantId}/members/${userId}`, updateData)
  },

  // Remove merchant member
  removeMerchantMember: async (merchantId: number, userId: number) => {
    return apiClient.delete(`/merchant/members/${merchantId}/members/${userId}`)
  },

  // 获取商家汇总统计
  getMerchantSummary: async () => {
    return apiClient.get('/merchant/merchants/summary')
  },

}




// WebSocket connection for real-time updates
export class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private reconnectInterval = 5000
  private shouldReconnect = true

  constructor(url: string) {
    this.url = url
  }

  connect() {
    try {
      this.ws = new WebSocket(this.url)

      this.ws.onopen = () => {
        console.log('WebSocket connected')
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMessage(data)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      this.ws.onclose = () => {
        console.log('WebSocket disconnected')
        if (this.shouldReconnect) {
          setTimeout(() => this.connect(), this.reconnectInterval)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
    }
  }

  disconnect() {
    this.shouldReconnect = false
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  send(message: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    }
  }

  private handleMessage(data: any) {
    // Handle different message types
    switch (data.type) {
      case 'notification':
        // Handle notification
        break
      case 'system_update':
        // Handle system update
        break
      default:
        console.log('Unknown message type:', data.type)
    }
  }
}

// AI World management API
export const aiWorldApi = {
  // Get AI worlds list
  getWorlds: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
  }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/ai/worlds', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // Get AI world by ID
  getWorld: async (id: string) => {
    return apiClient.get(`/ai/worlds/${id}`)
  },

  // Create AI world
  createWorld: async (worldData: any) => {
    return apiClient.post('/ai/worlds', worldData)
  },

  // Update AI world
  updateWorld: async (id: string, worldData: any) => {
    return apiClient.put(`/ai/worlds/${id}`, worldData)
  },

  // Delete AI world
  deleteWorld: async (id: string) => {
    return apiClient.delete(`/ai/worlds/${id}`)
  },

  // Start AI world
  startWorld: async (id: string) => {
    return apiClient.post(`/ai/worlds/${id}/start`)
  },

  // Stop AI world
  stopWorld: async (id: string) => {
    return apiClient.post(`/ai/worlds/${id}/stop`)
  },

  // Pause AI world
  pauseWorld: async (id: string) => {
    return apiClient.post(`/ai/worlds/${id}/pause`)
  },

  // Get AI world events
  getWorldEvents: async (id: string, params?: { limit?: number }) => {
    return apiClient.get(`/ai/worlds/${id}/events`, { params })
  },

  // Get AI world memories
  getWorldMemories: async (id: string, params?: { type?: string; limit?: number }) => {
    return apiClient.get(`/ai/worlds/${id}/memories`, { params })
  },

  // Get AI world stats
  getWorldStats: async (id: string) => {
    return apiClient.get(`/ai/worlds/${id}/stats`)
  },
}

// Multiplayer Rooms API
export const roomApi = {
  // Create room
  createRoom: async (roomData: any) => {
    return apiClient.post('/ai/rooms', roomData)
  },

  // List rooms
  getRooms: async (params?: { world_id?: string; status?: string; page?: number; limit?: number }) => {
    return apiClient.get('/ai/rooms', { params })
  },

  // Get room by ID
  getRoom: async (roomId: string) => {
    return apiClient.get(`/ai/rooms/${roomId}`)
  },

  // Join room
  joinRoom: async (joinData: { room_id: string; auth_id: string; password?: string }) => {
    return apiClient.post('/ai/rooms/join', joinData)
  },

  // Leave room
  leaveRoom: async (roomId: string, authId: string) => {
    return apiClient.post(`/ai/rooms/${roomId}/leave?auth_id=${authId}`)
  },

  // Start room
  startRoom: async (roomId: string) => {
    return apiClient.post(`/ai/rooms/${roomId}/start`)
  },

  // Get player room
  getPlayerRoom: async (authId: string) => {
    return apiClient.get(`/ai/rooms/player/${authId}/room`)
  },
}

// Agent Management API
export const agentApi = {
  // Get agents list
  getAgents: async (params?: { page?: number; limit?: number; search?: string }) => {
    return apiClient.get('/agents', { params })
  },

  // Get agent by ID
  getAgent: async (id: string) => {
    return apiClient.get(`/agents/${id}`)
  },

  // Create agent
  createAgent: async (agentData: any) => {
    return apiClient.post('/agents', agentData)
  },

  // Update agent
  updateAgent: async (id: string, agentData: any) => {
    return apiClient.put(`/agents/${id}`, agentData)
  },

  // Delete agent
  deleteAgent: async (id: string) => {
    return apiClient.delete(`/agents/${id}`)
  },

  // Clone agent
  cloneAgent: async (id: string) => {
    return apiClient.post(`/agents/${id}/clone`)
  },

  // Converse with agent
  converse: async (id: string, message: string) => {
    return apiClient.post(`/agents/${id}/converse`, { message })
  },

  // Submit feedback
  submitFeedback: async (id: string, feedback: any) => {
    return apiClient.post(`/agents/${id}/feedback`, feedback)
  },

  // Get agent stats
  getAgentStats: async () => {
    return apiClient.get('/agents/stats/overview')
  },

  // Knowledge management
  createKnowledge: async (knowledgeData: any) => {
    return apiClient.post('/agents/knowledge', knowledgeData)
  },

  getKnowledgeList: async (params?: { page?: number; limit?: number }) => {
    return apiClient.get('/agents/knowledge', { params })
  },

  getKnowledge: async (id: string) => {
    return apiClient.get(`/agents/knowledge/${id}`)
  },

  updateKnowledge: async (id: string, knowledgeData: any) => {
    return apiClient.put(`/agents/knowledge/${id}`, knowledgeData)
  },

  deleteKnowledge: async (id: string) => {
    return apiClient.delete(`/agents/knowledge/${id}`)
  },

  rebuildEmbedding: async (id: string) => {
    return apiClient.post(`/agents/knowledge/${id}/rebuild-embedding`)
  },

  // Dialogue management
  createDialogue: async (dialogueData: any) => {
    return apiClient.post('/agents/dialogues', dialogueData)
  },

  getDialogues: async (params?: { page?: number; limit?: number }) => {
    return apiClient.get('/agents/dialogues', { params })
  },

  getDialogue: async (id: string) => {
    return apiClient.get(`/agents/dialogues/${id}`)
  },

  deleteDialogue: async (id: string) => {
    return apiClient.delete(`/agents/dialogues/${id}`)
  },

  getSessionDialogues: async (sessionId: string) => {
    return apiClient.get(`/agents/dialogues/sessions/${sessionId}`)
  },

  getAgentUsageStats: async () => {
    return apiClient.get('/agents/dialogues/stats/usage')
  },

  // Market management
  createMarketSale: async (saleData: any) => {
    return apiClient.post('/agents/market', saleData)
  },

  getMarketSales: async (params?: { page?: number; limit?: number }) => {
    return apiClient.get('/agents/market', { params })
  },

  getMarketSale: async (id: string) => {
    return apiClient.get(`/agents/market/${id}`)
  },

  updateMarketSale: async (id: string, saleData: any) => {
    return apiClient.put(`/agents/market/${id}`, saleData)
  },

  deleteMarketSale: async (id: string) => {
    return apiClient.delete(`/agents/market/${id}`)
  },

  getRevenueStats: async () => {
    return apiClient.get('/agents/market/stats/revenue')
  },
}

// Ad Management API
export const adApi = {
  // Analytics
  getAnalyticsOverview: async () => {
    return apiClient.get('/ads/analytics/overview')
  },

  getPerformanceMetrics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/ads/analytics/performance', { params })
  },

  getTopCampaigns: async (params?: { limit?: number }) => {
    return apiClient.get('/ads/analytics/top-campaigns', { params })
  },

  getGeographicBreakdown: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/ads/analytics/geographic-breakdown', { params })
  },

  getDeviceBreakdown: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/ads/analytics/device-breakdown', { params })
  },

  // Campaign creatives
  listCreatives: async (campaignId: string, params?: { page?: number; limit?: number }) => {
    return apiClient.get(`/ads/campaigns/${campaignId}/creatives`, { params })
  },

  createCreative: async (campaignId: string, creativeData: any) => {
    return apiClient.post(`/ads/campaigns/${campaignId}/creatives`, creativeData)
  },

  getCreative: async (creativeId: string) => {
    return apiClient.get(`/ads/creatives/${creativeId}`)
  },

  updateCreative: async (creativeId: string, creativeData: any) => {
    return apiClient.put(`/ads/creatives/${creativeId}`, creativeData)
  },

  deleteCreative: async (creativeId: string) => {
    return apiClient.delete(`/ads/creatives/${creativeId}`)
  },

  // Campaign targeting
  listTargetings: async (campaignId: string, params?: { page?: number; limit?: number }) => {
    return apiClient.get(`/ads/campaigns/${campaignId}/targetings`, { params })
  },

  createTargeting: async (campaignId: string, targetingData: any) => {
    return apiClient.post(`/ads/campaigns/${campaignId}/targetings`, targetingData)
  },

  getTargeting: async (targetingId: string) => {
    return apiClient.get(`/ads/targetings/${targetingId}`)
  },

  updateTargeting: async (targetingId: string, targetingData: any) => {
    return apiClient.put(`/ads/targetings/${targetingId}`, targetingData)
  },

  deleteTargeting: async (targetingId: string) => {
    return apiClient.delete(`/ads/targetings/${targetingId}`)
  },

  // Impressions
  getImpressions: async (params?: { page?: number; limit?: number; campaign_id?: string }) => {
    return apiClient.get('/ads/impressions', { params })
  },

  // Tracking
  trackImpression: async (trackingData: any) => {
    return apiClient.post('/ads/track/impression', trackingData)
  },

  // Campaigns
  getCampaigns: async (params?: { page?: number; limit?: number; search?: string }) => {
    return apiClient.get('/ads/campaigns', { params })
  },

  createCampaign: async (campaignData: any) => {
    return apiClient.post('/ads/campaigns', campaignData)
  },

  updateCampaign: async (campaignId: string, campaignData: any) => {
    return apiClient.put(`/ads/campaigns/${campaignId}`, campaignData)
  },

  deleteCampaign: async (campaignId: string) => {
    return apiClient.delete(`/ads/campaigns/${campaignId}`)
  },

  getCampaign: async (campaignId: string) => {
    return apiClient.get(`/ads/campaigns/${campaignId}`)
  },

  // Creatives
  getCreatives: async (params?: { page?: number; limit?: number; campaign_id?: string }) => {
    return apiClient.get('/ads/creatives', { params })
  },
}

// Notification Management API
export const notificationApi = {
  // Get notifications list
  getNotifications: async (params?: { page?: number; limit?: number; status?: string }) => {
    return apiClient.get('/notifications', { params })
  },

  // Get notification by ID
  getNotification: async (id: string) => {
    return apiClient.get(`/notifications/${id}`)
  },

  // Create notification
  createNotification: async (notificationData: any) => {
    return apiClient.post('/notifications', notificationData)
  },

  // Update notification
  updateNotification: async (id: string, notificationData: any) => {
    return apiClient.put(`/notifications/${id}`, notificationData)
  },

  // Delete notification
  deleteNotification: async (id: string) => {
    return apiClient.delete(`/notifications/${id}`)
  },

  // Mark as read
  markAsRead: async (id: string) => {
    return apiClient.put(`/notifications/${id}/read`)
  },

  // Mark all as read
  markAllAsRead: async () => {
    return apiClient.delete('/notifications/read-all')
  },

  // Archive notification
  archiveNotification: async (id: string) => {
    return apiClient.put(`/notifications/${id}/archive`)
  },

  // Broadcast notification
  broadcastNotification: async (broadcastData: any) => {
    return apiClient.post('/notifications/broadcast', broadcastData)
  },

  // Send notification
  sendNotification: async (sendData: any) => {
    return apiClient.post('/notifications/send', sendData)
  },

  // Get queue status
  getQueueStatus: async () => {
    return apiClient.get('/notifications/queue/status')
  },

  // Get notification stats
  getNotificationStats: async () => {
    return apiClient.get('/notifications/stats/overview')
  },

  // Templates management
  createTemplate: async (templateData: any) => {
    return apiClient.post('/notifications/templates', templateData)
  },

  getTemplates: async (params?: { page?: number; limit?: number }) => {
    return apiClient.get('/notifications/templates', { params })
  },

  getTemplate: async (id: string) => {
    return apiClient.get(`/notifications/templates/${id}`)
  },

  updateTemplate: async (id: string, templateData: any) => {
    return apiClient.put(`/notifications/templates/${id}`, templateData)
  },

  deleteTemplate: async (id: string) => {
    return apiClient.delete(`/notifications/templates/${id}`)
  },

  previewTemplate: async (id: string, previewData: any) => {
    return apiClient.post(`/notifications/templates/${id}/preview`, previewData)
  },

  // Settings management
  getSettings: async () => {
    return apiClient.get('/notifications/settings')
  },

  createSetting: async (settingData: any) => {
    return apiClient.post('/notifications/settings', settingData)
  },

  updateSetting: async (id: string, settingData: any) => {
    return apiClient.put(`/notifications/settings/${id}`, settingData)
  },

  deleteSetting: async (id: string) => {
    return apiClient.delete(`/notifications/settings/${id}`)
  },

  checkQuietHours: async () => {
    return apiClient.get('/notifications/settings/check-quiet-hours')
  },
}

// File Management API
export const fileApi = {
  // Get files list
  getFiles: async (params?: { page?: number; limit?: number; folder_id?: string }) => {
    return apiClient.get('/files', { params })
  },

  // Get file by ID
  getFile: async (id: string) => {
    return apiClient.get(`/files/${id}`)
  },

  // Upload file
  uploadFile: async (fileData: FormData) => {
    return apiClient.post('/files/upload', fileData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Batch upload files
  batchUploadFiles: async (filesData: FormData) => {
    return apiClient.post('/files/upload/batch', filesData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Delete file
  deleteFile: async (id: string) => {
    return apiClient.delete(`/files/${id}`)
  },

  // Restore file
  restoreFile: async (id: string) => {
    return apiClient.post(`/files/${id}/restore`)
  },

  // Create file share
  createFileShare: async (id: string, shareData: any) => {
    return apiClient.post(`/files/${id}/share`, shareData)
  },

  // Get file shares
  getFileShares: async (id: string) => {
    return apiClient.get(`/files/${id}/shares`)
  },

  // Delete file share
  deleteFileShare: async (shareId: string) => {
    return apiClient.delete(`/files/shares/${shareId}`)
  },

  // Create file version
  createFileVersion: async (id: string, versionData: any) => {
    return apiClient.post(`/files/${id}/versions`, versionData)
  },

  // Get file versions
  getFileVersions: async (id: string) => {
    return apiClient.get(`/files/${id}/versions`)
  },

  // Get file stats
  getFileStats: async () => {
    return apiClient.get('/files/stats/overview')
  },

  // Create file metadata
  createFile: async (fileData: any) => {
    return apiClient.post('/files/metadata', fileData)
  },

  // Update file metadata
  updateFile: async (id: string, fileData: any) => {
    return apiClient.put(`/files/${id}/metadata`, fileData)
  },
}

// OSS File Management API (Enhanced)
export const ossFileApi = {
  // Get OSS files list (enhanced - with preview_url, search, file_type)
  getFiles: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    file_type?: string;
    include_deleted?: boolean;
  }) => {
    return apiClient.get('/oss/files', { params })
  },

  // Upload file to OSS
  uploadFile: async (formData: FormData, isPublic: boolean = false) => {
    const endpoint = isPublic ? '/oss/upload/public' : '/oss/upload/private'
    return apiClient.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Delete OSS file (soft delete by default)
  deleteFile: async (id: number, hard?: boolean) => {
    return apiClient.delete(`/oss/files/${id}`, { params: { hard: hard || false } })
  },
}

// Folder Management API
export const folderApi = {
  // Get folders list
  getFolders: async (params?: { page?: number; page_size?: number }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/folders', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // Get folder by ID
  getFolder: async (id: string) => {
    return apiClient.get(`/folders/${id}`)
  },

  // Create folder
  createFolder: async (folderData: any) => {
    return apiClient.post('/folders', folderData)
  },

  // Delete folder
  deleteFolder: async (id: string) => {
    return apiClient.delete(`/folders/${id}`)
  },
}

// Role & Permission Management API
export const roleApi = {
  // Get roles list
  getRoles: async (params?: { page?: number; page_size?: number }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/roles', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // Get role by ID
  getRole: async (id: string) => {
    return apiClient.get(`/roles/${id}`)
  },

  // Create role
  createRole: async (roleData: any) => {
    return apiClient.post('/roles', roleData)
  },

  // Update role
  updateRole: async (id: string, roleData: any) => {
    return apiClient.put(`/roles/${id}`, roleData)
  },

  // Delete role
  deleteRole: async (id: string) => {
    return apiClient.delete(`/roles/${id}`)
  },

  // Get permissions list
  getPermissions: async (params?: { page?: number; page_size?: number }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/roles/permissions', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // Get permission by ID
  getPermission: async (id: string) => {
    return apiClient.get(`/roles/permissions/${id}`)
  },

  // Create permission
  createPermission: async (permissionData: any) => {
    return apiClient.post('/roles/permissions', permissionData)
  },

  // Update permission
  updatePermission: async (id: string, permissionData: any) => {
    return apiClient.put(`/roles/permissions/${id}`, permissionData)
  },

  // Delete permission
  deletePermission: async (id: string) => {
    return apiClient.delete(`/roles/permissions/${id}`)
  },

  // Get users list
  getUsers: async (params?: { page?: number; limit?: number; search?: string }) => {
    return apiClient.get('/users', { params })
  },

  // Update user role
  updateUserRole: async (userId: string, data: { role_id: string }) => {
    return apiClient.put(`/users/${userId}/role`, data)
  },
}

// Enhanced Analytics API
export const analyticsApiEnhanced = {
  // Get dashboard stats
  getDashboardStats: async () => {
    return apiClient.get('/analytics/dashboard')
  },

  // Get user analytics
  getUserAnalytics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/users', { params })
  },

  // Get revenue analytics
  getRevenueAnalytics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/revenue', { params })
  },

  // Get script analytics
  getScriptAnalytics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/scripts', { params })
  },

  // Get venue analytics
  getVenueAnalytics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/venues', { params })
  },

  // Get merchant analytics
  getMerchantAnalytics: async (params?: { start_date?: string; end_date?: string }) => {
    return apiClient.get('/analytics/merchants', { params })
  },
}

// Merchant Rooms API (商家房间管理 - 三级联动)
export const merchantRoomApi = {
  // 获取商家列表(用于级联选择)
  getMerchantsForRooms: async () => {
    return apiClient.get('/merchant/merchants')
  },

  // 根据商家获取场馆列表
  getVenuesByMerchant: async (merchantId: number) => {
    return apiClient.get(`/merchant/venues/merchant/${merchantId}`)
  },

  // 根据场馆获取房间列表
  getRoomsByVenue: async (venueId: number, params?: { page?: number; page_size?: number; supports_ai_world?: boolean; include_deleted?: boolean }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/merchant/rooms', {
      params: { venue_id: venueId, page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // 创建房间
  createRoom: async (roomData: any) => {
    return apiClient.post('/merchant/rooms', roomData)
  },

  // 更新房间
  updateRoom: async (roomId: number, roomData: any) => {
    return apiClient.put(`/merchant/rooms/${roomId}`, roomData)
  },

  // 删除房间
  deleteRoom: async (roomId: number) => {
    return apiClient.delete(`/merchant/rooms/${roomId}`)
  },

  // 批量检查房间数据一致性
  batchCheckRoomsConsistency: async (roomIds: number[]) => {
    return apiClient.get('/merchant/rooms/rooms/consistency/batch-check', {
      params: { room_ids: roomIds.join(',') }
    })
  },

  // 强制关闭房间
  forceCloseRoom: async (roomId: number) => {
    return apiClient.post(`/merchant/rooms/rooms/${roomId}/force-close`)
  }
}

// Merchant Venues API (商家场馆管理)
export const merchantVenueApi = {
  // 获取商家列表(用于筛选)
  getMerchants: async (params?: { page?: number; page_size?: number }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/merchant/merchants', {
      params: { page: 1, limit: page_size || 100, ...restParams }
    })
  },

  // 获取场馆列表
  getVenues: async (params?: {
    merchant_id?: number
    status?: string
    search?: string
    page?: number
    page_size?: number
    limit?: number
  }) => {
    // 将 page_size 映射为 limit（后端参数名是 limit）
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/merchant/venues', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    })
  },

  // 获取场馆详情
  getVenue: async (venueId: number) => {
    return apiClient.get(`/merchant/venues/${venueId}`)
  },

  // 创建场馆
  createVenue: async (venueData: any) => {
    return apiClient.post('/merchant/venues', venueData)
  },

  // 更新场馆
  updateVenue: async (venueId: number, venueData: any) => {
    return apiClient.put(`/merchant/venues/${venueId}`, venueData)
  },

  // 删除场馆
  deleteVenue: async (venueId: number) => {
    return apiClient.delete(`/merchant/venues/${venueId}`)
  }
}

// Admin Social Monitor API (管理员社交监控)
export const adminSocialMonitorApi = {
  // ========== 三级联动接口 ==========
  // 获取商户列表（用于联动选择）
  getMerchantsForRooms: async () => {
    return apiClient.get('/merchant/merchants');
  },

  // 根据商户获取场馆列表（用于联动选择）
  getVenuesByMerchant: async (merchantId: number) => {
    return apiClient.get(`/merchant/venues/merchant/${merchantId}`);
  },

  // 根据场馆获取房间列表（用于联动选择）
  getRoomsByVenue: async (venueId: number) => {
    return apiClient.get('/merchant/rooms', { params: { venue_id: venueId } });
  },

  // ========== 聊天监控 ==========
  // 查询所有房间的聊天会话
  getChatSessions: async (params?: {
    room_id?: number;
    instance_id?: string;
    session_type?: string;
    room_status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/admin/chat/sessions', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    });
  },

  // 以指定身份发送消息
  sendMessageAs: async (data: {
    room_id: number;
    session_id: string;
    sender_id: number;
    sender_type: 'npc' | 'player' | 'admin';
    message_type: 'text' | 'image' | 'system';
    content: string;
    is_admin_msg?: boolean;
    attachments?: Array<{
      type: string;
      url: string;
      size?: number;
      filename?: string;
    }>;
  }) => {
    return apiClient.post('/admin/chat/messages/send-as', data);
  },

  // ========== 朋友圈监控 ==========
  // 查询所有房间的朋友圈帖子
  getSocialPosts: async (params?: {
    room_id?: number;
    instance_id?: string;
    author_type?: 'npc' | 'player';
    post_type?: string;
    room_status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/admin/social/posts', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    });
  },

  // 以指定身份发布帖子
  createPostAs: async (data: {
    room_id: number;
    author_id: number;
    author_type: 'npc' | 'player';
    post_type: 'user' | 'task' | 'achievement' | 'location' | 'system';
    content: string;
    attachments?: Array<{
      type: string;
      url: string;
    }>;
  }) => {
    return apiClient.post('/admin/social/posts/create-as', data);
  },

  // ========== NPC 管理 ==========
  // 获取可用的 NPC 列表
  getNPCs: async (params?: {
    world_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }) => {
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/admin/npcs', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    });
  },

  // ========== 操作日志 ==========
  // 查询管理员操作日志
  getOperationLogs: async (params?: {
    operation_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const { page_size, ...restParams } = params || {}
    return apiClient.get('/admin/operation-logs', {
      params: { page: 1, limit: page_size || 20, ...restParams }
    });
  },

  // ========== 辅助接口 ==========
  // 获取房间列表（用于筛选）
  getRooms: async (params?: {
    merchant_id?: number;
    venue_id?: number;
    status?: string;
  }) => {
    return apiClient.get('/merchant/rooms', { params });
  },
};

// AI世界数据管理 API (admin-portal-api 后端)
export const aiWorldDataApi = {
  // ========== 玩家管理 ==========
  // 获取玩家列表
  getPlayers: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    occupation?: string;
    status?: string;
  }) => {
    return apiClient.get(`/admin/players/${worldInstanceId}/list`, { params });
  },

  // 获取玩家详情
  getPlayer: async (worldInstanceId: string, characterId: number) => {
    return apiClient.get(`/admin/players/${worldInstanceId}/${characterId}`);
  },

  // 获取玩家统计
  getPlayerStats: async (worldInstanceId: string) => {
    return apiClient.get(`/admin/players/${worldInstanceId}/statistics`);
  },

  // ========== 背包物品管理 ==========
  // 获取物品列表 (后端路径: /inventory/{id}/items)
  getItems: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    character_id?: number;
    item_type?: string;
  }) => {
    return apiClient.get(`/admin/inventory/${worldInstanceId}/items`, { params });
  },

  // 获取物品详情 (后端路径: /inventory/{id}/players/{character_id})
  getItem: async (worldInstanceId: string, itemId: number) => {
    return apiClient.get(`/admin/inventory/${worldInstanceId}/players/${itemId}`);
  },

  // ========== 技能管理 ==========
  // 获取技能列表
  getSkills: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    character_id?: number;
    skill_type?: string;
  }) => {
    return apiClient.get(`/admin/players/${worldInstanceId}/skills`, { params });
  },

  // 获取技能详情
  getSkill: async (worldInstanceId: string, skillId: number) => {
    return apiClient.get(`/admin/players/${worldInstanceId}/skills/${skillId}`);
  },

  // ========== 任务管理 ==========
  // 获取任务列表 (后端路径: /tasks/{id}/instances)
  getTasks: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    character_id?: number;
    status?: string;
    task_type?: string;
  }) => {
    return apiClient.get(`/admin/tasks/${worldInstanceId}/instances`, { params });
  },

  // 获取任务详情
  getTask: async (worldInstanceId: string, taskId: number) => {
    return apiClient.get(`/admin/tasks/${worldInstanceId}/instances/${taskId}`);
  },

  // ========== 社交关系管理 ==========
  // 获取社交关系列表 (后端路径: /social/{id}/moments)
  getSocialConnections: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    character_id?: number;
    connection_type?: string;
  }) => {
    return apiClient.get(`/admin/social/${worldInstanceId}/moments`, { params });
  },

  // 获取社交详情
  getSocialConnection: async (worldInstanceId: string, connectionId: number) => {
    return apiClient.get(`/admin/social/${worldInstanceId}/moments/${connectionId}`);
  },

  // ========== 邀请管理 ==========
  // 获取邀请列表 (后端路径: /invites/{id}/tokens)
  getInvites: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    token_type?: string;
  }) => {
    return apiClient.get(`/admin/invites/${worldInstanceId}/tokens`, { params });
  },

  // 获取邀请详情
  getInvite: async (worldInstanceId: string, tokenId: string) => {
    return apiClient.get(`/admin/invites/${worldInstanceId}/tokens/${tokenId}`);
  },

  // 创建邀请
  createInvite: async (worldInstanceId: string, data: any) => {
    return apiClient.post(`/admin/invites/${worldInstanceId}/tokens`, data);
  },

  // 更新邀请
  updateInvite: async (worldInstanceId: string, tokenId: string, data: any) => {
    return apiClient.put(`/admin/invites/${worldInstanceId}/tokens/${tokenId}`, data);
  },

  // 删除邀请
  deleteInvite: async (worldInstanceId: string, tokenId: string) => {
    return apiClient.delete(`/admin/invites/${worldInstanceId}/tokens/${tokenId}`);
  },

  // ========== 宝箱会话管理 ==========
  // 获取宝箱会话列表 (后端路径: /treasures/{id}/sessions)
  getTreasures: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => {
    return apiClient.get(`/admin/treasures/${worldInstanceId}/sessions`, { params });
  },

  // 获取宝箱详情
  getTreasure: async (worldInstanceId: string, sessionId: string) => {
    return apiClient.get(`/admin/treasures/${worldInstanceId}/sessions/${sessionId}`);
  },

  // 创建宝箱会话
  createTreasure: async (worldInstanceId: string, data: any) => {
    return apiClient.post(`/admin/treasures/${worldInstanceId}/sessions`, data);
  },

  // 更新宝箱会话
  updateTreasure: async (worldInstanceId: string, sessionId: string, data: any) => {
    return apiClient.put(`/admin/treasures/${worldInstanceId}/sessions/${sessionId}`, data);
  },

  // 删除宝箱会话
  deleteTreasure: async (worldInstanceId: string, sessionId: string) => {
    return apiClient.delete(`/admin/treasures/${worldInstanceId}/sessions/${sessionId}`);
  },

  // ========== 模板管理 ==========
  // 获取任务模板列表
  getTaskTemplates: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    task_type?: string;
  }) => {
    return apiClient.get(`/admin/templates/${worldInstanceId}/tasks`, { params });
  },

  // 获取物品模板列表
  getItemTemplates: async (worldInstanceId: string, params?: {
    page?: number;
    limit?: number;
    item_type?: string;
    rarity?: string;
  }) => {
    return apiClient.get(`/admin/templates/${worldInstanceId}/items`, { params });
  },

  // 获取模板详情
  getTemplate: async (worldInstanceId: string, templateId: string, templateType: string) => {
    return apiClient.get(`/admin/templates/${worldInstanceId}/${templateType}/${templateId}`);
  },

  // 创建任务模板
  createTaskTemplate: async (worldInstanceId: string, data: any) => {
    return apiClient.post(`/admin/templates/${worldInstanceId}/tasks`, data);
  },

  // 更新任务模板
  updateTaskTemplate: async (worldInstanceId: string, templateId: string, data: any) => {
    return apiClient.put(`/admin/templates/${worldInstanceId}/tasks/${templateId}`, data);
  },

  // 删除任务模板
  deleteTaskTemplate: async (worldInstanceId: string, templateId: string) => {
    return apiClient.delete(`/admin/templates/${worldInstanceId}/tasks/${templateId}`);
  },

  // 创建物品模板
  createItemTemplate: async (worldInstanceId: string, data: any) => {
    return apiClient.post(`/admin/templates/${worldInstanceId}/items`, data);
  },

  // 更新物品模板
  updateItemTemplate: async (worldInstanceId: string, templateId: string, data: any) => {
    return apiClient.put(`/admin/templates/${worldInstanceId}/items/${templateId}`, data);
  },

  // 删除物品模板
  deleteItemTemplate: async (worldInstanceId: string, templateId: string) => {
    return apiClient.delete(`/admin/templates/${worldInstanceId}/items/${templateId}`);
  },

  // ========== 数据导出 ==========
  // 导出玩家
  exportPlayers: async (worldInstanceId: string) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/players`, {
      responseType: 'blob'
    });
  },

  // 导出背包
  exportInventory: async (worldInstanceId: string, characterId?: number) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/inventory`, {
      params: { character_id: characterId },
      responseType: 'blob'
    });
  },

  // 导出技能
  exportSkills: async (worldInstanceId: string, characterId?: number) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/skills`, {
      params: { character_id: characterId },
      responseType: 'blob'
    });
  },

  // 导出任务
  exportTasks: async (worldInstanceId: string, characterId?: number) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/tasks`, {
      params: { character_id: characterId },
      responseType: 'blob'
    });
  },

  // 导出社交
  exportSocial: async (worldInstanceId: string) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/social`, {
      responseType: 'blob'
    });
  },

  // 导出邀请
  exportInvites: async (worldInstanceId: string) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/invites`, {
      responseType: 'blob'
    });
  },

  // 导出宝箱
  exportTreasures: async (worldInstanceId: string) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/treasures`, {
      responseType: 'blob'
    });
  },

  // 导出模板
  exportTemplates: async (worldInstanceId: string, templateType?: string) => {
    return apiClient.get(`/admin/export/${worldInstanceId}/templates`, {
      params: { template_type: templateType },
      responseType: 'blob'
    });
  },
};

// ========== 票务管理 API (Ticketing) ==========
/**
 * 票务管理相关接口
 * 包括：积分管理、配置管理（退票规则、购票规则、充值赠送）
 */
export const ticketingApi = {
  // ==================== 积分管理 ====================

  /**
   * 获取积分概览统计
   * GET /admin/points/summary
   */
  getPointsSummary: async (): Promise<AxiosResponse<PointsSummary>> => {
    return apiClient.get('/admin/points/summary')
  },

  /**
   * 获取积分流水列表
   * GET /admin/points/transactions/list
   */
  getPointsTransactions: async (
    params?: TransactionsQueryParams
  ): Promise<AxiosResponse<PointsTransactionsList>> => {
    return apiClient.get('/admin/points/transactions/list', { params })
  },

  /**
   * 获取用户积分详情
   * GET /admin/points/user/{userId}/points
   */
  getUserPoints: async (
    userId: number
  ): Promise<AxiosResponse<UserPointsDetail>> => {
    return apiClient.get(`/admin/points/user/${userId}/points`)
  },

  /**
   * 调整用户积分
   * POST /admin/points/user/{userId}/points/adjust
   */
  adjustUserPoints: async (
    userId: number,
    data: AdjustPointsRequest
  ): Promise<AxiosResponse<AdjustPointsResponse>> => {
    return apiClient.post(`/admin/points/user/${userId}/points/adjust`, data)
  },

  /**
   * 获取用户积分流水
   * GET /admin/points/user/{userId}/points/transactions
   */
  getUserPointsTransactions: async (
    userId: number,
    params?: PageQueryParams
  ): Promise<AxiosResponse<UserPointsTransactions>> => {
    return apiClient.get(`/admin/points/user/${userId}/points/transactions`, { params })
  },

  // ==================== 配置管理 ====================

  /**
   * 获取退票规则
   * GET /admin/configs/refund-policies
   */
  getRefundPolicies: async (): Promise<AxiosResponse<RefundPoliciesResponse>> => {
    return apiClient.get('/admin/configs/refund-policies')
  },

  /**
   * 更新退票规则
   * PUT /admin/configs/refund-policies
   */
  updateRefundPolicies: async (
    policies: RefundPolicy[]
  ): Promise<AxiosResponse<any>> => {
    return apiClient.put('/admin/configs/refund-policies', policies)
  },

  /**
   * 添加退票规则
   * POST /admin/configs/refund-policies/add
   */
  addRefundPolicy: async (
    policy: Omit<RefundPolicy, 'policy_name'>
  ): Promise<AxiosResponse<any>> => {
    return apiClient.post('/admin/configs/refund-policies/add', policy)
  },

  /**
   * 删除退票规则
   * DELETE /admin/configs/refund-policies/{policyName}
   */
  deleteRefundPolicy: async (
    policyName: string
  ): Promise<AxiosResponse<any>> => {
    return apiClient.delete(`/admin/configs/refund-policies/${policyName}`)
  },

  /**
   * 获取购票规则
   * GET /admin/configs/booking-rules
   */
  getBookingRules: async (): Promise<AxiosResponse<BookingRulesResponse>> => {
    return apiClient.get('/admin/configs/booking-rules')
  },

  /**
   * 更新购票规则
   * PUT /admin/configs/booking-rules
   */
  updateBookingRules: async (
    rules: UpdateBookingRulesRequest
  ): Promise<AxiosResponse<any>> => {
    return apiClient.put('/admin/configs/booking-rules', rules)
  },

  /**
   * 获取充值赠送档次
   * GET /admin/configs/recharge-bonus-tiers
   */
  getRechargeBonusTiers: async (): Promise<AxiosResponse<BonusTiersResponse>> => {
    return apiClient.get('/admin/configs/recharge-bonus-tiers')
  },

  /**
   * 更新充值赠送档次
   * PUT /admin/configs/recharge-bonus-tiers
   */
  updateRechargeBonusTiers: async (
    tiers: BonusTier[]
  ): Promise<AxiosResponse<any>> => {
    return apiClient.put('/admin/configs/recharge-bonus-tiers', tiers)
  },

  /**
   * 获取积分汇率
   * GET /admin/configs/points-exchange-rate
   */
  getPointsExchangeRate: async (): Promise<AxiosResponse<ExchangeRate>> => {
    return apiClient.get('/admin/configs/points-exchange-rate')
  },

  /**
   * 更新积分汇率
   * PUT /admin/configs/points-exchange-rate
   */
  updatePointsExchangeRate: async (
    data: UpdateExchangeRateRequest
  ): Promise<AxiosResponse<any>> => {
    return apiClient.put('/admin/configs/points-exchange-rate', data)
  },
};

export default apiClient

// ========== SystemConfig 管理 API ==========
export const systemConfigApi = {
  // 获取配置列表
  getConfigs: async (params?: { page?: number; page_size?: number; search?: string }) => {
    return apiClient.get('/admin/configs/system-configs', { params })
  },

  // 创建配置
  createConfig: async (data: {
    config_key: string
    config_value: Record<string, any>
    description?: string
  }) => {
    return apiClient.post('/admin/configs/system-configs', data)
  },

  // 更新配置
  updateConfig: async (key: string, data: {
    config_value?: Record<string, any>
    description?: string
  }) => {
    return apiClient.put(`/admin/configs/system-configs/${key}`, data)
  },

  // 删除配置
  deleteConfig: async (key: string) => {
    return apiClient.delete(`/admin/configs/system-configs/${key}`)
  },

  // 初始化默认配置
  initDefaults: async () => {
    return apiClient.post('/admin/configs/system-configs/init-defaults')
  },

  // 获取VIP等级配置
  getVIPLevels: async () => {
    return apiClient.get('/admin/configs/system-configs/vip_level_config')
  },

  // 更新VIP等级配置
  updateVIPLevels: async (data: { levels: any[] }) => {
    return apiClient.put('/admin/configs/system-configs/vip_level_config/batch', data)
  },
}

// ========== 榜单/祝福语管理 API ==========
export const leaderboardApi = {
  // 获取祝福语列表
  getBlessings: async (params?: {
    page?: number
    page_size?: number
    performer_id?: number
    status?: string
  }) => {
    return apiClient.get('/admin/leaderboard/blessings', { params })
  },

  // 获取祝福语详情
  getBlessingById: async (id: number) => {
    return apiClient.get(`/admin/leaderboard/blessings/${id}`)
  },

  // 审核祝福语
  reviewBlessing: async (id: number, data: {
    status: 'approved' | 'rejected'
    review_note?: string
  }) => {
    return apiClient.post(`/admin/leaderboard/blessings/${id}/review`, data)
  },

  // 获取祝福语阶梯配置
  getBlessingTiers: async () => {
    return apiClient.get('/admin/leaderboard/blessings/tiers/config')
  },
}

// ========== 匿名配置管理 API ==========
export const anonymousApi = {
  // 获取匿名配置
  getConfig: async () => {
    return apiClient.get('/admin/ticketing/anonymous-config')
  },

  // 更新匿名配置
  updateConfig: async (data: {
    enabled?: boolean
    nickname_prefixes?: string[]
    avatars?: Array<{ url: string; gender: string }>
    require_gender?: boolean
  }) => {
    return apiClient.put('/admin/ticketing/anonymous-config', data)
  },
}

// ========== 付费墙配置管理 API ==========
export const paywallAdminApi = {
  // 获取付费墙配置
  getConfig: async () => {
    return apiClient.get('/admin/paywall/config')
  },

  // 更新付费墙配置
  updateConfig: async (data: {
    features: Array<{
      feature_key: string
      feature_name: string
      description: string
      required_vip_level: number
      is_enabled: boolean
    }>
  }) => {
    return apiClient.put('/admin/paywall/config', data)
  },
}

// ========== 场次价格管理 API ==========
export const sessionPricingApi = {
  // 获取场次列表（带价格信息）
  getSessions: async (params?: {
    page?: number
    page_size?: number
    script_id?: number
    venue_id?: number
    start_date?: string
    end_date?: string
  }) => {
    return apiClient.get('/admin/sessions/list', { params })
  },

  // 更新场次价格
  updatePricing: async (id: number, data: {
    original_price?: number
    vip_price?: number
    discount_price?: number | null
    vip_discount_price?: number | null
    discount_enabled?: boolean
    discount_start_time?: string | null
    discount_end_time?: string | null
  }) => {
    return apiClient.put(`/admin/sessions/${id}`, data)
  },
}

// ========== 场次数据只读查看 API ==========
export const sessionDataApi = {
  // 获取场次列表（监控只读前缀，避免命中 archived_guard）
  getSessions: async (
    params?: SessionsQueryParams
  ): Promise<AxiosResponse<SessionsListResponse>> => {
    return apiClient.get('/monitoring/session-data/sessions', { params })
  },

  // 获取单个场次详情，包含 session_slots 的 JSON / image 字段
  getSessionDetail: async (
    id: number
  ): Promise<AxiosResponse<TicketingSessionDetail>> => {
    return apiClient.get(`/monitoring/session-data/sessions/${id}`)
  },
}

// ========== 剧本配置块管理 API (Config Blocks) ==========
export const configBlockApi = {
  // 获取配置块列表
  getBlocks: async (params?: {
    package_id?: string
    config_type?: string
    config_key?: string
    is_active?: boolean
    page?: number
    limit?: number
    order_by?: string
    order_dir?: string
    script_id?: number  // 剧本ID，支持直接通过剧本ID查询配置块
    script_package_id?: number  // 剧本包数据库ID
  }) => {
    return apiClient.get('/playwright/config-blocks', { params })
  },

  // 获取配置块详情
  getBlock: async (blockId: number) => {
    return apiClient.get(`/playwright/config-blocks/${blockId}`)
  },

  // 根据 key 获取配置块
  getBlockByKey: async (packageId: string, configType: string, configKey: string) => {
    return apiClient.get(`/playwright/config-blocks/by-key/${packageId}/${configType}/${configKey}`)
  },

  // 按类型分组获取配置块
  getBlocksByType: async (packageId: string) => {
    return apiClient.get(`/playwright/config-blocks/by-type/${packageId}`)
  },

  // 创建配置块
  createBlock: async (blockData: {
    package_id: string
    config_type: string
    config_key: string
    config_name: string
    config_data: Record<string, any>
    sort_order?: number
    extra_metadata?: Record<string, any>
  }) => {
    return apiClient.post('/playwright/config-blocks', blockData)
  },

  // 更新配置块
  updateBlock: async (blockId: number, blockData: {
    config_name?: string
    config_data?: Record<string, any>
    sort_order?: number
    is_active?: boolean
    extra_metadata?: Record<string, any>
  }) => {
    return apiClient.put(`/playwright/config-blocks/${blockId}`, blockData)
  },

  // 删除配置块
  deleteBlock: async (blockId: number, hard?: boolean) => {
    return apiClient.delete(`/playwright/config-blocks/${blockId}`, { params: { hard } })
  },

  // 批量创建配置块
  batchCreateBlocks: async (blocks: Array<{
    package_id: string
    config_type: string
    config_key: string
    config_name: string
    config_data: Record<string, any>
    sort_order?: number
    extra_metadata?: Record<string, any>
  }>) => {
    return apiClient.post('/playwright/config-blocks/batch', blocks)
  },

  // 导入配置块
  importBlocks: async (packageId: string, configBlocks: Array<{
    config_type: string
    config_key: string
    config_name: string
    config_data: Record<string, any>
    sort_order?: number
    extra_metadata?: Record<string, any>
  }>) => {
    return apiClient.post(`/playwright/config-blocks/import/${packageId}`, configBlocks)
  },

  // 导出配置块
  exportBlocks: async (packageId: string, configType?: string) => {
    return apiClient.get(`/playwright/config-blocks/export/${packageId}`, { params: { config_type: configType } })
  },

  // 获取配置块统计
  getStats: async (packageId: string) => {
    return apiClient.get(`/playwright/config-blocks/stats/${packageId}`)
  },

  // 获取配置块统计（通过 script_id）
  getStatsByScript: async (scriptId: number) => {
    return apiClient.get(`/playwright/config-blocks/stats/script/${scriptId}`)
  },

  // 获取剧本包的所有配置块
  getPackageBlocks: async (packageDbId: number) => {
    return apiClient.get(`/playwright/config-blocks/package/${packageDbId}/blocks`)
  },

  // 删除剧本包的所有配置块
  deleteBlocksByPackage: async (packageId: string, configType?: string) => {
    return apiClient.delete(`/playwright/config-blocks/by-package/${packageId}`, { params: { config_type: configType } })
  },

  // 导出剧本包到 OSS (ZIP + YAML)
  exportPackageToMinio: async (packageId: string, version?: string) => {
    return apiClient.post(`/playwright/config-blocks/export/package/${packageId}`, null, { params: { version } })
  },
}

// ========== 玩家回放 API (Phase 2: ai-engine outbox → MQTT → portal.game_records_sync) ==========
// 数据源: portal.game_records_sync (record_type ∈ {player_event, conversation_utterance, player_summary_report})
// 受众: 仅管理员/GM (后端 require_admin 守卫)
// 端点前缀: /admin/playback/*
import type {
  PlaybackEventListResponse,
  PlaybackImageListResponse,
  PlaybackPageQueryParams,
  PlaybackSessionDetail,
  PlaybackSessionListResponse,
  PlaybackSessionsQueryParams,
  PlaybackTimelineQueryParams,
  PlaybackTimelineResponse,
  PlaybackUtteranceListResponse,
} from '@/types/playback'

export const playbackApi = {
  /** 列出回放会话（按 world_id/player_id/merchant_id 过滤） */
  listSessions: async (
    params?: PlaybackSessionsQueryParams
  ): Promise<AxiosResponse<PlaybackSessionListResponse>> => {
    return apiClient.get('/admin/playback/sessions', { params })
  },

  /** 单个 (world, player) 会话元数据；404 表示无回放记录 */
  getSession: async (
    worldId: string,
    playerId: string
  ): Promise<AxiosResponse<PlaybackSessionDetail>> => {
    return apiClient.get(`/admin/playback/sessions/${worldId}/${playerId}`)
  },

  /** 统一时间线（events + ASR + 截图合并，按 occurred_at 升序，游标分页） */
  getTimeline: async (
    worldId: string,
    playerId: string,
    params?: PlaybackTimelineQueryParams
  ): Promise<AxiosResponse<PlaybackTimelineResponse>> => {
    return apiClient.get(`/admin/playback/sessions/${worldId}/${playerId}/timeline`, { params })
  },

  /** 仅 player_events（offset 分页） */
  getEvents: async (
    worldId: string,
    playerId: string,
    params?: PlaybackPageQueryParams
  ): Promise<AxiosResponse<PlaybackEventListResponse>> => {
    return apiClient.get(`/admin/playback/sessions/${worldId}/${playerId}/events`, { params })
  },

  /** 仅 ASR（offset 分页） */
  getUtterances: async (
    worldId: string,
    playerId: string,
    params?: PlaybackPageQueryParams
  ): Promise<AxiosResponse<PlaybackUtteranceListResponse>> => {
    return apiClient.get(`/admin/playback/sessions/${worldId}/${playerId}/utterances`, { params })
  },

  /** 仅截图列表（按 report_version + session_slot_uuid 分组） */
  getImages: async (
    worldId: string,
    playerId: string
  ): Promise<AxiosResponse<PlaybackImageListResponse>> => {
    return apiClient.get(`/admin/playback/sessions/${worldId}/${playerId}/images`)
  },
}

// ========== 向后兼容导出 ==========
// adminPortalApi 是 apiClient 的别名，用于 Contents.tsx 等旧代码
export { apiClient as adminPortalApi }
